import React, { useContext, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Text,
  Button,
  Center,
  Stack,
  Group,
  ThemeIcon,
  Divider,
  Title,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconClock,
  IconCopy,
  IconCircleCheck,
  IconArrowRight,
  IconMail,
} from "@tabler/icons-react";
import { AppContext } from "../../Pages/Wrapper";
import { Colors } from "../../Lib/Data";
import MegaLoader from "../../Components/MegaLoader";

const SuccessfulPayment: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  const [status, setStatus] = useState<"loading" | "success">("loading");

  const subscriptionId = searchParams.get("subscription_id");
  const paymentId = searchParams.get("payment_id");

  useEffect(() => {
    // With Dodo Payments, the user is redirected back here after payment.
    // The actual transaction creation happens via webhook (subscription.active).
    const timer = setTimeout(() => {
      setStatus("success");
      context?.reloadProfile();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (status === "loading") {
    return <MegaLoader />;
  }

  return (
    <Container size="sm" py={80}>
      <Paper
        radius="lg"
        p={0}
        withBorder
        className={`payment-success-card ${theme}`}
        style={{
          overflow: "hidden",
          border: `1px solid ${theme === "dark" ? "#373A40" : "#E9ECEF"}`,
          boxShadow: theme === "dark" ? "0 10px 30px rgba(0,0,0,0.5)" : "0 10px 30px rgba(0,0,0,0.05)",
        }}
      >
        {/* Header Gradient */}
        <div
          style={{
            background: `linear-gradient(135deg, ${Colors.GreenPrimary} 0%, #2b8a3e 100%)`,
            padding: "40px 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ThemeIcon
            size={80}
            radius={100}
            color="white"
            variant="white"
            style={{
              boxShadow: "0 8px 20px rgba(0,0,0,0.15)",
              color: Colors.GreenPrimary,
            }}
          >
            <IconCheck size={50} stroke={3} />
          </ThemeIcon>
          <Title
            order={2}
            mt="xl"
            style={{ color: "white", textAlign: "center", fontSize: 32 }}
          >
            Payment Successful!
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.9)", textAlign: "center" }} mt="xs">
            Your transaction has been received and is being processed.
          </Text>
        </div>

        <Stack p={40} gap="xl">
          <section>
            <Title order={4} mb="md" fw={600}>
              What happens next?
            </Title>
            <Stack gap="md">
              <Group wrap="nowrap" align="flex-start">
                <ThemeIcon size={28} radius="xl" color="blue" variant="light">
                  <IconMail size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    Check your email
                  </Text>
                  <Text size="sm" c="dimmed">
                    We've sent a payment receipt and a subscription notification to your inbox.
                  </Text>
                </div>
              </Group>

              <Group wrap="nowrap" align="flex-start">
                <ThemeIcon size={28} radius="xl" color="orange" variant="light">
                  <IconClock size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    Wait for activation
                  </Text>
                  <Text size="sm" c="dimmed">
                    Our team is setting up your access. This usually takes 5-15 minutes.
                  </Text>
                </div>
              </Group>

              <Group wrap="nowrap" align="flex-start">
                <ThemeIcon size={28} radius="xl" color="green" variant="light">
                  <IconCircleCheck size={16} />
                </ThemeIcon>
                <div>
                  <Text size="sm" fw={600}>
                    Start enjoying!
                  </Text>
                  <Text size="sm" c="dimmed">
                    Once activated, you can view your login details in the subscriptions section.
                  </Text>
                </div>
              </Group>
            </Stack>
          </section>

          {(subscriptionId || paymentId) && (
            <>
              <Divider label="Transaction Details" labelPosition="center" />
              <Paper
                withBorder
                p="md"
                radius="md"
                bg={theme === "dark" ? "dark.8" : "gray.0"}
              >
                <Stack gap="xs">
                  {subscriptionId && (
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={500}>
                        SUBSCRIPTION ID
                      </Text>
                      <Group gap={4}>
                        <Text size="xs" fw={600} style={{ fontFamily: "monospace" }}>
                          {subscriptionId}
                        </Text>
                        <CopyButton value={subscriptionId} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="right">
                              <ActionIcon color={copied ? "teal" : "gray"} variant="subtle" onClick={copy} size="xs">
                                <IconCopy size={12} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    </Group>
                  )}
                  {paymentId && (
                    <Group justify="space-between">
                      <Text size="xs" c="dimmed" fw={500}>
                        PAYMENT ID
                      </Text>
                      <Group gap={4}>
                        <Text size="xs" fw={600} style={{ fontFamily: "monospace" }}>
                          {paymentId}
                        </Text>
                        <CopyButton value={paymentId} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Copied" : "Copy"} withArrow position="right">
                              <ActionIcon color={copied ? "teal" : "gray"} variant="subtle" onClick={copy} size="xs">
                                <IconCopy size={12} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                      </Group>
                    </Group>
                  )}
                </Stack>
              </Paper>
            </>
          )}

          <Group grow mt="md">
            <Button
              size="lg"
              variant="light"
              color="gray"
              onClick={() => navigate("/d/transactions")}
              radius="md"
            >
              My Transactions
            </Button>
            <Button
              size="lg"
              color={Colors.GreenPrimary}
              onClick={() => navigate("/d/subscriptions")}
              rightSection={<IconArrowRight size={18} />}
              radius="md"
              style={{ boxShadow: `0 4px 15px ${Colors.GreenSecondary}` }}
            >
              Go to Dashboard
            </Button>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
};

export default SuccessfulPayment;
